import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Menu, ChevronDown, LogOut, Sun, Moon, Monitor } from "lucide-react";

import { logout } from "../features/auth/authSlice";
import { MENU_GROUPS } from "./menuConfig";
import { useTheme } from "../theme/ThemeContext";

const THEME_CHOICES = [
    { value: "light", label: "Terang", icon: Sun },
    { value: "dark", label: "Gelap", icon: Moon },
    { value: "system", label: "Sistem", icon: Monitor }
];

// judul halaman diambil dari struktur menu
const findPage = (pathname) => {
    for (const group of MENU_GROUPS) {
        for (const item of group.items) {
            if (pathname.includes(`/${item.to}`)) {
                return { group: group.title, label: item.label };
            }
        }
    }
    return { group: null, label: "Admin" };
};

export default function Header({ onMenuClick }) {
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef();
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const user = useSelector((state) => state.auth?.user);

    const name = user?.name || user?.username || "User";
    const page = findPage(pathname);
    const { theme, isDark, setTheme, toggleTheme } = useTheme();

    useEffect(() => {
        const handleClick = (e) => {
            if (!dropdownRef.current?.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    return (
        <header className="h-16 shrink-0 flex items-center justify-between gap-4 bg-white border-b border-line px-4 md:px-6 sticky top-0 z-30 no-print print:hidden">

            <div className="flex items-center gap-3 min-w-0">
                <button
                    type="button"
                    onClick={onMenuClick}
                    className="md:hidden p-2 -ml-2 rounded-md text-muted hover:bg-surface"
                    aria-label="Buka menu"
                >
                    <Menu size={20} />
                </button>

                <div className="min-w-0">
                    {page.group && (
                        <div className="text-xs text-muted leading-none mb-1">{page.group}</div>
                    )}
                    <h1 className="text-base md:text-lg font-semibold text-body leading-none truncate">
                        {page.label}
                    </h1>
                </div>
            </div>

            <div className="flex items-center gap-1">
                <button
                    type="button"
                    onClick={toggleTheme}
                    className="p-2 rounded-md text-muted hover:bg-surface hover:text-body"
                    title={isDark ? "Ganti ke mode terang" : "Ganti ke mode gelap"}
                    aria-label={isDark ? "Ganti ke mode terang" : "Ganti ke mode gelap"}
                >
                    {isDark ? <Sun size={18} /> : <Moon size={18} />}
                </button>

                <div className="relative" ref={dropdownRef}>
                    <button
                        type="button"
                        onClick={() => setOpen(!open)}
                        className="flex items-center gap-2 rounded-md pl-1 pr-2 py-1 hover:bg-surface"
                        aria-haspopup="menu"
                        aria-expanded={open}
                    >
                        <span className="w-8 h-8 rounded-full bg-ink text-white text-sm font-semibold flex items-center justify-center">
                            {name.charAt(0).toUpperCase()}
                        </span>
                        <span className="hidden sm:block text-sm font-medium text-body max-w-[160px] truncate">
                            {name}
                        </span>
                        <ChevronDown size={16} className="text-muted" />
                    </button>

                    {open && (
                        <div
                            role="menu"
                            className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-line p-1.5"
                        >
                            <div className="px-3 py-2 border-b border-line mb-1">
                                <div className="text-xs text-muted">Masuk sebagai</div>
                                <div className="text-sm font-semibold text-body truncate">{name}</div>
                            </div>

                            <div className="px-3 pt-1 pb-2 border-b border-line mb-1">
                                <div className="text-xs text-muted mb-1.5" id="theme-setting-label">Tampilan</div>
                                <div
                                    role="radiogroup"
                                    aria-labelledby="theme-setting-label"
                                    className="grid grid-cols-3 gap-1 p-1 rounded-md bg-surface"
                                >
                                    {THEME_CHOICES.map(({ value, label, icon: Icon }) => {
                                        const active = theme === value;
                                        return (
                                            <button
                                                key={value}
                                                type="button"
                                                role="radio"
                                                aria-checked={active}
                                                onClick={() => setTheme(value)}
                                                className={`flex flex-col items-center gap-1 rounded py-1.5 text-[11px] font-medium transition-colors ${
                                                    active
                                                        ? "bg-card text-body shadow-sm"
                                                        : "text-muted hover:text-body"
                                                }`}
                                            >
                                                <Icon size={15} />
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <button
                                type="button"
                                role="menuitem"
                                onClick={() => dispatch(logout())}
                                className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md"
                            >
                                <LogOut size={16} />
                                Keluar
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
}
